import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useVideoUpload } from '@/hooks/use-video-upload';
import { HlsPlayer } from './HlsPlayer';
import { ExternalVideoEmbed } from './ExternalVideoEmbed';
import { VideoRecorder } from './VideoRecorder';
import { Trash2, Upload, Video, Link as LinkIcon, Loader2, AlertCircle, Youtube, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import type { ExternalLinkRequest } from '@/services/introVideoService';

const ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MIN_SIZE_BYTES = 1024; // 1 KB — matches the backend's IntroVideoService.MIN_FILE_SIZE floor (SCRUM-301)
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

interface IntroVideoUploadProps {
  accessToken: string;
  readOnly?: boolean;
  onRemove?: () => void;
  teleprompterScript?: string;
}

function detectPlatform(url: string): ExternalLinkRequest['platform'] | null {
  if (/youtube\.com|youtu\.be/.test(url)) return 'YOUTUBE';
  if (/vimeo\.com/.test(url)) return 'VIMEO';
  if (/instagram\.com\/(reel|p)\//.test(url)) return 'INSTAGRAM';
  return null;
}

export function IntroVideoUpload({ accessToken, readOnly = false, onRemove, teleprompterScript }: IntroVideoUploadProps) {
  const {
    uploadState,
    progress,
    error,
    videoStatus,
    upload,
    submitLink,
    remove,
    retry,
  } = useVideoUpload({ accessToken });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'record' | 'link'>('upload');

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error('Please select a video file (MP4, WebM, MOV)');
        return;
      }
      if (file.size < MIN_SIZE_BYTES) {
        toast.error('File is too small to be a valid video');
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        toast.error(`File size exceeds ${MAX_SIZE_MB} MB limit`);
        return;
      }
      upload(file);
    },
    [upload]
  );

  const handleRecordingComplete = useCallback((blob: Blob) => {
    setRecordedBlob(blob);
  }, []);

  const handleUploadRecording = useCallback(() => {
    if (recordedBlob) {
      const file = new File([recordedBlob], 'recording.webm', {
        type: recordedBlob.type || 'video/webm',
      });
      upload(file);
      setRecordedBlob(null);
    }
  }, [recordedBlob, upload]);

  const handleSubmitLink = useCallback(() => {
    const platform = detectPlatform(linkUrl);
    if (!platform) {
      toast.error('Please enter a valid YouTube, Vimeo, or Instagram URL');
      return;
    }
    submitLink({ url: linkUrl, platform });
    setLinkUrl('');
  }, [linkUrl, submitLink]);

  // ── Ready state ──────────────────────────────────────────────────────────────
  if (uploadState === 'ready' && videoStatus?.status === 'READY') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-1">
          <CheckCircle2 className="w-4 h-4" />
          Intro video active
        </div>
        {videoStatus.sourceType === 'EXTERNAL_LINK' && videoStatus.platform ? (
          <ExternalVideoEmbed
            platform={videoStatus.platform}
            externalUrl={videoStatus.externalUrl!}
            oembedHtml={videoStatus.oembedHtml}
          />
        ) : videoStatus.playbackUrl ? (
          videoStatus.playbackUrl.includes('.m3u8')
            ? <HlsPlayer src={videoStatus.playbackUrl} />
            : <video src={videoStatus.playbackUrl} controls playsInline className="w-full rounded-xl" />
        ) : (
          <div className="rounded-xl bg-muted p-6 text-center text-muted-foreground text-sm">
            Video uploaded successfully
          </div>
        )}
        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await remove(); onRemove?.(); }}
            className="text-destructive hover:text-destructive hover:bg-destructive/5 border-destructive/30"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Remove video
          </Button>
        )}
      </div>
    );
  }

  // ── Processing ───────────────────────────────────────────────────────────────
  if (uploadState === 'processing') {
    return (
      <div className="rounded-xl bg-muted/40 border border-border p-8 text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="text-sm font-medium text-foreground">Processing your video…</p>
        <p className="text-xs text-muted-foreground">This usually takes less than a minute.</p>
      </div>
    );
  }

  // ── Uploading ────────────────────────────────────────────────────────────────
  if (uploadState === 'uploading') {
    return (
      <div className="rounded-xl bg-muted/40 border border-border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Uploading video</p>
            <Progress value={progress} className="mt-2 h-1.5" />
          </div>
          <span className="text-xs font-mono text-muted-foreground shrink-0">{progress}%</span>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (uploadState === 'failed' || uploadState === 'error') {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-5 space-y-3">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm font-medium">{error || 'Something went wrong'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={retry}>Try again</Button>
      </div>
    );
  }

  // ── Idle — 3 method selector ─────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Method buttons — same style as photo upload buttons */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5${activeTab === 'upload' ? ' bg-foreground text-background hover:bg-foreground/90 hover:text-background border-foreground' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          <Upload className="w-3.5 h-3.5" /> Upload
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5${activeTab === 'record' ? ' bg-foreground text-background hover:bg-foreground/90 hover:text-background border-foreground' : ''}`}
          onClick={() => setActiveTab('record')}
        >
          <Video className="w-3.5 h-3.5" /> Record
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5${activeTab === 'link' ? ' bg-foreground text-background hover:bg-foreground/90 hover:text-background border-foreground' : ''}`}
          onClick={() => setActiveTab('link')}
        >
          <LinkIcon className="w-3.5 h-3.5" /> Paste Link
        </Button>
      </div>

      {/* Upload panel */}
      {activeTab === 'upload' && (
        <>
          <div
            onClick={() => fileInputRef.current?.click()}
            className="group relative border-2 border-dashed border-border hover:border-primary/50 rounded-xl bg-muted/20 hover:bg-primary/[0.03] transition-all duration-200 cursor-pointer p-10 text-center"
          >
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/15 transition-colors">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Click to browse or drag & drop</p>
            <p className="text-xs text-muted-foreground mt-1">MP4, WebM, MOV — max {MAX_SIZE_MB} MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={handleFileSelect}
            className="hidden"
          />
        </>
      )}

      {/* Record panel */}
      {activeTab === 'record' && (
        <div className="space-y-3">
          <VideoRecorder
            maxDurationSeconds={40}
            onRecordingComplete={handleRecordingComplete}
            teleprompterScript={teleprompterScript}
          />
          {recordedBlob && (
            <Button onClick={handleUploadRecording} className="w-full">
              Use this recording
            </Button>
          )}
        </div>
      )}

      {/* Link panel */}
      {activeTab === 'link' && (
        <div className="rounded-xl border border-border bg-muted/20 p-5 space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Paste a video URL</p>
            <p className="text-xs text-muted-foreground">Works with YouTube, Vimeo, and Instagram Reels.</p>
          </div>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSubmitLink} disabled={!linkUrl.trim()}>
              Add
            </Button>
          </div>
          <div className="flex items-center gap-3 pt-1">
            {[
              { label: 'YouTube', color: 'text-red-500' },
              { label: 'Vimeo', color: 'text-indigo-500' },
              { label: 'Instagram', color: 'text-pink-500' },
            ].map(p => (
              <span key={p.label} className={`text-xs font-semibold ${p.color} opacity-70`}>{p.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
